import React, { useEffect, useRef, useState } from 'react';
import Footer from '../components/Footer';
import './Collaboration.css';

function FadeUp({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`fade-up ${visible ? 'fade-up--visible' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export default function Collaboration({ navigate }) {
  const curriculum = [
    {
      icon: '🥁',
      title: 'Rhythm Basics',
      desc: 'Students are introduced to foundational rhythm concepts through interactive games and activities.',
    },
    {
      icon: '🎼',
      title: 'Note Recognition',
      desc: 'Simple, approachable lessons on notes and pitch designed to feel like play, not pressure.',
    },
    {
      icon: '🎁',
      title: 'Free Instruments',
      desc: 'Every student takes home a free recorder or maraca to keep exploring music after the workshop ends.',
    },
    {
      icon: '⏱',
      title: 'One-Hour Sessions',
      desc: 'Each workshop is a focused, hour-long experience that fits comfortably into a school or care schedule.',
    },
  ];

  const trackRecord = [
    { place: 'Novi Public Library', desc: 'Multiple workshops introducing K-8 students to music theory through trivia, Kahoot, and hands-on games.' },
    { place: 'Detroit Public Library', desc: 'Recurring sessions for students in grades K-6, each ending with a final performance for parents.' },
    { place: 'We Rock the Spectrum Novi', desc: 'An indoor gym for children on the autism spectrum, where workshops were adapted for a sensory-friendly environment.' },
  ];

  return (
    <div className="collab-page">
      {/* Hero */}
      <div className="page-hero collab-hero">
        <div className="page-hero-ghost">Harmony</div>
        <div className="section-tag">ECHO x PediaCare Bridge</div>
        <h1>Healing Through <em>Harmony</em></h1>
        <p>
          A workshop series dedicated to kick-starting musical passions in students with
          disabilities or ailments, brought to life through a collaboration between
          PediaCare Bridge and ECHO Music Empowerment.
        </p>
      </div>

      {/* About the collaboration */}
      <section className="collab-about">
        <FadeUp className="collab-about-text">
          <div className="section-tag">About</div>
          <h2 className="section-h2">Two organizations, <em>one mission</em></h2>
          <p>
            Healing Through Harmony is a collaborative effort between PediaCare Bridge and
            ECHO Music Empowerment, two youth-led organizations committed to supporting
            children's education. Together, we're building a workshop curriculum that
            introduces music in a welcoming, low-pressure way to students who might not
            otherwise have access to it.
          </p>
          <p>
            Each workshop is an hour-long session where students are introduced to the
            basics of rhythm and notes, and every participant takes home a free recorder or
            maraca to keep the music going at home.
          </p>
        </FadeUp>
        <FadeUp delay={100} className="collab-about-visual">
          <div className="collab-visual-card">
            <div className="collab-visual-icon">💜</div>
            <h3>Backed By</h3>
            <p>
              We're proud to be supported by the <strong>Karma for Cara Foundation</strong>,
              whose backing is helping make this expanded workshop series possible.
            </p>
          </div>
        </FadeUp>
      </section>

      {/* Curriculum */}
      <section className="collab-pillars">
        <FadeUp>
          <div className="section-tag">The Curriculum</div>
          <h2 className="section-h2">What each <em>workshop</em> looks like</h2>
        </FadeUp>
        <div className="collab-pillars-grid">
          {curriculum.map((p, i) => (
            <FadeUp key={i} delay={i * 100} className="collab-pillar-card">
              <div className="collab-pillar-icon">{p.icon}</div>
              <h3>{p.title}</h3>
              <p>{p.desc}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Track record */}
      <section className="collab-track">
        <FadeUp>
          <div className="section-tag">Proven Success</div>
          <h2 className="section-h2">Where we've <em>hosted</em></h2>
          <p className="collab-track-intro">
            ECHO's workshop curriculum has already seen established success at multiple
            locations, giving us a strong foundation to build on as we expand to pediatric
            patients and students with disabilities.
          </p>
        </FadeUp>
        <div className="collab-track-grid">
          {trackRecord.map((t, i) => (
            <FadeUp key={i} delay={i * 100} className="collab-track-card">
              <div className="collab-track-marker">📍</div>
              <h3>{t.place}</h3>
              <p>{t.desc}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Expansion */}
      <section className="collab-expansion">
        <FadeUp className="collab-expansion-inner">
          <div className="section-tag">What's Next</div>
          <h2 className="section-h2">Expanding the <em>impact</em></h2>
          <p>
            We're excited to expand this curriculum to pediatric patients and students with
            disabilities. From <strong>June 28th to September 10th</strong>, we will be hosting
            <strong> 20 workshops</strong> at various locations, specifically prioritizing
            patients to cultivate joy through music.
          </p>
          <div className="collab-stat-row">
            <div className="collab-stat">
              <div className="collab-stat-num">20</div>
              <div className="collab-stat-label">Workshops Planned</div>
            </div>
            <div className="collab-stat">
              <div className="collab-stat-num">Jun 28 – Sep 10</div>
              <div className="collab-stat-label">Program Window</div>
            </div>
            <div className="collab-stat">
              <div className="collab-stat-num">1 hr</div>
              <div className="collab-stat-label">Per Session</div>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* Partners */}
      <section className="collab-partners">
        <FadeUp>
          <div className="section-tag">Past Partners</div>
          <h2 className="section-h2">Organizations we've <em>worked with</em></h2>
        </FadeUp>
        <div className="collab-partners-grid">
          {[
            {
              name: 'Karma for Cara Foundation',
              desc: 'Gracious funder backing our Healing Through Harmony workshop series for pediatric patients and students with disabilities.',
            },
            {
              name: 'PediaCare Bridge',
              desc: 'Youth-led organization dedicated to supporting children\'s health and education, co-hosting our Healing Through Harmony workshops.',
            },
            {
              name: 'Strings of Hope Detroit',
              desc: 'A Metro Detroit initiative bringing live music performances to seniors at assisted living facilities across the region.',
            },
            {
              name: 'We Rock the Spectrum Novi',
              desc: 'An inclusive indoor gym for children on the autism spectrum, where ECHO hosted sensory-friendly music camps.',
            },
          ].map((p, i) => (
            <FadeUp key={i} delay={i * 80} className="collab-partner-card">
              <h3>{p.name}</h3>
              <p>{p.desc}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Get involved CTA */}
      <section className="collab-cta">
        <FadeUp className="collab-cta-inner">
          <div className="section-tag" style={{ color: 'rgba(255,255,255,0.75)' }}>Get Involved</div>
          <h2 className="section-h2 collab-cta-heading">Want to <em>volunteer?</em></h2>
          <p>
            If you're a musician interested in bringing comfort to pediatric patients and
            students with disabilities through music, we'd love to hear from you.
          </p>
          <a href="mailto:shareyoursound01@gmail.com" className="btn-primary collab-cta-btn">
            Contact Us →
          </a>
        </FadeUp>
      </section>

      <Footer navigate={navigate} />
    </div>
  );
}
